"use client";

import { FilmIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import type { SocialProviderAccount } from "../hooks/useSocialProviderAccounts";
import AccountAvatar from "./AccountAvatar";
import ConfirmAction from "./ConfirmAction";
import { formatScheduleDateTimeLabel } from "./ScheduleDateLabel";
import Typography from "./Typography";
import type { PublicationSpacingPost } from "./publicationFlow/publicationSpacing";

export default function PublicationSpacingDialog({ posts, accounts, publishNow, onConfirm, onCancel, thumbnailUrl }: {
  posts: readonly PublicationSpacingPost[];
  accounts: readonly SocialProviderAccount[];
  publishNow: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  thumbnailUrl?: (post: PublicationSpacingPost) => string | undefined;
}) {
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(() => new Set());
  return (
    <ConfirmAction
      title="Posts are close together"
      description="These posts are close together on the same account. Publishing in quick succession may trigger platform limits."
      confirmLabel={publishNow ? "Post now" : "Keep timing"}
      cancelLabel="Change time"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <ul aria-label="Posts less than an hour apart" className="mt-4 flex max-h-[45vh] flex-col gap-2 overflow-y-auto">
        {posts.map(post => {
          const account = accounts.find(account => account.id === post.accountId && account.provider === post.provider);
          const label = account?.username || account?.name || "Account unavailable";
          const src = thumbnailUrl ? thumbnailUrl(post) : post.projectId
            ? `${process.env.NEXT_PUBLIC_API_URL}/projects/${post.projectId}/thumbnail` : undefined;
          return (
            <li key={post.id} className="flex min-w-0 items-center gap-2.5 rounded-xl border border-slate-200 p-[5px] pr-3 dark:border-surface-tertiary">
              <div className="flex h-12 aspect-[9/16] shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-200 dark:bg-surface-tertiary">
                {src && !failedThumbnails.has(post.id) ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={src} alt="" className="h-full w-full object-cover"
                    onError={() => setFailedThumbnails(current => new Set(current).add(post.id))} />
                ) : <FilmIcon className="size-4 text-foreground-muted" aria-hidden />}
              </div>
              <div className="min-w-0 flex-1">
                <Typography variant="button2" component="p" className="truncate">{label}</Typography>
                <time dateTime={new Date(post.scheduledAt).toISOString()} className="mt-0.5 block text-xs text-foreground-secondary">
                  {formatScheduleDateTimeLabel(post.scheduledAt)}
                </time>
              </div>
              <AccountAvatar account={account ?? { provider: post.provider }} size="s" />
            </li>
          );
        })}
      </ul>
    </ConfirmAction>
  );
}
